/**
 * @remarks Formily Schema 转换器
 * @author ickeep <i@ickeep.com>
 * @module @yimoka/designable-playground
 */

import { TreeNode, type ITreeNode } from '@yimoka/designable-core';

import {
  DEFAULT_FORMILY_COMPONENT_NAME_ALIASES,
  HISTORICAL_FIELD_COMPONENT_NAME,
  IGNORED_HISTORICAL_FORMILY_COMPONENT_NAMES,
} from '../shared/formilyCompatibility';
import {
  assertSafeSchemaObjectKeys,
  FORMILY_SCHEMA_ARRAY_CHILD_KEYS,
  FORMILY_SCHEMA_RECORD_CHILD_KEYS,
  FORMILY_SCHEMA_SINGLE_CHILD_KEYS,
  INTERNAL_DESIGNABLE_SCHEMA_PROP_NAMES,
  isSafeSchemaObjectKey,
  isSchemaMetadataPropName,
  RESERVED_FORMILY_SCHEMA_PROP_NAMES,
} from '../shared/schema';
import type { ISchemaTransformer } from '../types';

export interface IFormilySchemaNode {
  items?: IFormilySchemaNode | IFormilySchemaNode[];
  type?: string;
  title?: string;
  properties?: Record<string, IFormilySchemaNode>;
  'x-component'?: string;
  'x-component-props'?: Record<string, unknown>;
  'x-designable-id'?: string;
  'x-index'?: number;
  [key: string]: unknown;
}

export interface IFormilySchemaDocument {
  type: 'object';
  properties?: Record<string, IFormilySchemaNode>;
  [key: string]: unknown;
}

export interface IFormilySchemaPackage {
  form?: Record<string, unknown>;
  schema: IFormilySchemaDocument;
}

export type IFormilySchemaInput = IFormilySchemaDocument | IFormilySchemaPackage;

export interface IFormilySchemaTransformerOptions {
  /** 根节点组件名 */
  rootComponentName?: string;
  /** 组件默认 schema type */
  componentTypes?: Record<string, string>;
  /** 组件默认装饰器，设置为 false 可关闭默认装饰器 */
  componentDecorators?: Record<string, string | false>;
  /** 兼容历史 Formily / Designable 组件名，导入时归一到当前设计器组件名 */
  componentNameAliases?: Record<string, string>;
  /** Schema type 缺少 x-component 时推断出的默认设计器组件名 */
  componentNameByType?: Record<string, string>;
  /** 数组项模板组件名 */
  arrayItemComponentName?: string;
  /** 字段名生成前缀 */
  fieldNamePrefix?: string;
}

const DEFAULT_COMPONENT_TYPES: Record<string, string> = {
  Input: 'string',
  InputSearch: 'string',
  TextArea: 'string',
  Password: 'string',
  AutoComplete: 'string',
  Search: 'string',
  Mentions: 'string',
  Text: 'void',
  Avatar: 'void',
  Alert: 'void',
  Badge: 'void',
  Divider: 'void',
  Empty: 'void',
  HTMLContent: 'void',
  Image: 'void',
  Link: 'void',
  QRCode: 'void',
  Statistic: 'void',
  Tag: 'void',
  Select: 'string',
  SearchSelect: 'string',
  ColorPicker: 'string',
  DatePicker: 'string',
  DateRangePicker: 'array',
  TimePicker: 'string',
  TimeRangePicker: 'array',
  NumberPicker: 'number',
  Cascader: 'array',
  TreeSelect: 'string',
  Transfer: 'array',
  Checkbox: 'boolean',
  Radio: 'string',
  Switch: 'boolean',
  Slider: 'number',
  Rate: 'number',
  Upload: 'array',
  Button: 'void',
  Form: 'object',
  FormCollapse: 'void',
  FormCollapsePanel: 'void',
  FormGrid: 'void',
  FormGridColumn: 'void',
  FormLayout: 'void',
  FormTab: 'void',
  'FormTab.TabPane': 'void',
  Card: 'void',
  GridRow: 'void',
  GridCol: 'void',
  Flex: 'void',
  Space: 'void',
  Tabs: 'void',
  TabPane: 'void',
  ArrayList: 'array',
  ArrayItem: 'void',
  ArrayCards: 'array',
  ArrayTable: 'array',
  ArrayTableColumn: 'void',
  Object: 'object',
};

const DEFAULT_COMPONENT_DECORATORS: Record<string, string> = {
  Cascader: 'FormItem',
  Checkbox: 'FormItem',
  DatePicker: 'FormItem',
  DateRangePicker: 'FormItem',
  AutoComplete: 'FormItem',
  ColorPicker: 'FormItem',
  Input: 'FormItem',
  InputSearch: 'FormItem',
  Mentions: 'FormItem',
  NumberPicker: 'FormItem',
  Password: 'FormItem',
  Radio: 'FormItem',
  Rate: 'FormItem',
  Select: 'FormItem',
  Search: 'FormItem',
  SearchSelect: 'FormItem',
  Slider: 'FormItem',
  Switch: 'FormItem',
  TextArea: 'FormItem',
  TimePicker: 'FormItem',
  TimeRangePicker: 'FormItem',
  Transfer: 'FormItem',
  TreeSelect: 'FormItem',
  Upload: 'FormItem',
};

const DEFAULT_COMPONENT_NAME_BY_TYPE: Record<string, string> = {
  string: 'Input',
  number: 'NumberPicker',
  integer: 'NumberPicker',
  boolean: 'Switch',
  array: 'ArrayList',
  object: 'Object',
  void: 'Text',
};

const STRING_VALUE_SCHEMA_COMPONENTS = new Set([
  'Cascader',
  'DateRangePicker',
  'TimeRangePicker',
  'Transfer',
]);

const RESERVED_COMPONENT_PROP_NAMES = new Set([
  'name',
  'title',
]);

const COMPONENT_RESERVED_PROP_NAMES_AS_COMPONENT_PROPS: Record<string, Set<string>> = {
  ArrayTableColumn: new Set(['title']),
  Statistic: new Set(['title']),
};

const isReservedComponentPropName = (
  componentName: string | undefined,
  key: string,
) => (
  RESERVED_COMPONENT_PROP_NAMES.has(key)
  && !(componentName && COMPONENT_RESERVED_PROP_NAMES_AS_COMPONENT_PROPS[componentName]?.has(key))
);

const SCHEMA_KEY_PROP = 'x-designable-schema-key';
const FORMILY_PACKAGE_PROP = 'x-designable-formily-package';
const FORMILY_ROOT_FORM_PROP = 'x-designable-root-form';
const FORMILY_ROOT_SCHEMA_PROP = 'x-designable-root-schema';
const FORMILY_ROOT_SCHEMA_FORM_PROP = 'x-designable-root-schema-form';
const FORMILY_SCHEMA_TYPE_PROP = 'x-designable-schema-type';
const FORMILY_STATE_HIDDEN_FROM_VISIBLE_PROP = 'x-designable-state-hidden-from-visible';
const FORMILY_SCALAR_ARRAY_ITEM_PROP = 'x-designable-scalar-array-item';
const FORMILY_SYNTHETIC_ARRAY_ITEM_PROP = 'x-designable-synthetic-array-item';

const INTERNAL_PROP_NAMES = INTERNAL_DESIGNABLE_SCHEMA_PROP_NAMES;

const clonePlainValue = <T>(value: T): T => {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value)) as T;
};

const clonePlainRecord = (value: unknown): Record<string, unknown> => (
  isRecord(value) ? clonePlainValue(value) : {}
);

const getStringProp = (
  value: Record<string, unknown>,
  key: string,
) => (
  typeof value[key] === 'string' && value[key].trim()
    ? value[key]
    : undefined
);

const getSafeSchemaKey = (value: unknown) => (
  typeof value === 'string'
  && value.trim()
  && isSafeSchemaObjectKey(value.trim())
    ? value.trim()
    : undefined
);

const omitUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (item !== undefined) {
      result[key] = item;
    }
  });
  return result;
};

const getSchemaKey = (
  node: TreeNode,
  index: number,
  fieldNamePrefix: string,
): string => {
  const name = getSafeSchemaKey(node.props?.name);
  if (name) return name;
  const schemaKey = getSafeSchemaKey(node.props?.[SCHEMA_KEY_PROP]);
  if (schemaKey) return schemaKey;
  return `${fieldNamePrefix}_${node.id || index}`;
};

const getUniqueSchemaKey = (
  properties: Record<string, IFormilySchemaNode>,
  key: string,
) => {
  const baseKey = getSafeSchemaKey(key) ?? 'field';
  if (!Object.prototype.hasOwnProperty.call(properties, baseKey)) return baseKey;

  let index = 2;
  let nextKey = `${baseKey}_${index}`;
  while (Object.prototype.hasOwnProperty.call(properties, nextKey)) {
    index += 1;
    nextKey = `${baseKey}_${index}`;
  }
  return nextKey;
};

const getExplicitSchemaType = (
  props: Record<string, unknown>,
) => (
  typeof props[FORMILY_SCHEMA_TYPE_PROP] === 'string'
    ? props[FORMILY_SCHEMA_TYPE_PROP]
    : undefined
);

/** 按设计器 valueType 把多选组件值映射为 Formily 字符串或数组类型。 */
const getMultiValueSchemaType = (props: Record<string, unknown>) => (
  props.valueType === 'string' ? 'string' : 'array'
);

const inferSelectSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
) => {
  if (componentName !== 'Select' && componentName !== 'SearchSelect') return undefined;
  if (props.mode !== 'multiple' && props.mode !== 'tags') return undefined;
  return getMultiValueSchemaType(props);
};

/** 根据静态选项、兼容 data 或 Store dataKey 判断 Checkbox 是否使用复选组值语义。 */
const inferCheckboxSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
) => {
  if (componentName !== 'Checkbox') return undefined;
  const hasGroupData = (Array.isArray(props.options) && props.options.length > 0)
    || (Array.isArray(props.data) && props.data.length > 0)
    || (typeof props.dataKey === 'string' && props.dataKey.trim().length > 0);
  if (!hasGroupData) return undefined;
  return getMultiValueSchemaType(props);
};

const inferTreeSelectSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
) => {
  if (componentName !== 'TreeSelect') return undefined;
  if (props.multiple !== true && props.treeCheckable !== true) return undefined;
  return 'array';
};

const inferStringValueSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
) => {
  if (!STRING_VALUE_SCHEMA_COMPONENTS.has(componentName)) return undefined;
  return props.valueType === 'string' ? 'string' : undefined;
};

const inferDateTimeSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
) => {
  if (componentName !== 'DatePicker' && componentName !== 'TimePicker') return undefined;
  if (componentName === 'DatePicker' && (props.dataValueType === 's' || props.dataValueType === 'ms')) {
    return 'number';
  }
  if (props.multiple !== true) return undefined;
  return getMultiValueSchemaType(props);
};

const inferComponentSchemaType = (
  componentName: string,
  props: Record<string, unknown>,
  componentTypes: Record<string, string>,
) => (
  inferSelectSchemaType(componentName, props)
  ?? inferCheckboxSchemaType(componentName, props)
  ?? inferTreeSelectSchemaType(componentName, props)
  ?? inferStringValueSchemaType(componentName, props)
  ?? inferDateTimeSchemaType(componentName, props)
  ?? componentTypes[componentName]
  ?? 'void'
);

const sortSchemaEntries = (
  schema: { properties?: Record<string, IFormilySchemaNode> },
): Array<[string, IFormilySchemaNode]> => (
  Object.entries(schema.properties ?? {})
    .map((entry, declarationIndex) => ({ declarationIndex, entry }))
    .sort((before, after) => {
      const beforeIndex = before.entry[1]['x-index'];
      const afterIndex = after.entry[1]['x-index'];

      if (typeof beforeIndex === 'number' && typeof afterIndex === 'number') {
        return beforeIndex - afterIndex || before.declarationIndex - after.declarationIndex;
      }

      return before.declarationIndex - after.declarationIndex;
    })
    .map(item => item.entry)
);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const isSchemaPropName = (key: string, componentName?: string) => (
  isSchemaMetadataPropName(key, componentName)
);

const pickSchemaProps = (
  value: Record<string, unknown>,
  componentName?: string,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (isSchemaPropName(key, componentName) && item !== undefined) {
      result[key] = item;
    }
  });
  return result;
};

const omitSchemaProps = (
  value: Record<string, unknown>,
  componentName?: string,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (
      !INTERNAL_PROP_NAMES.has(key)
      && !isSchemaPropName(key, componentName)
      && !isReservedComponentPropName(componentName, key)
    ) {
      result[key] = item;
    }
  });
  return result;
};

const omitInternalProps = (value: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (!INTERNAL_PROP_NAMES.has(key)) {
      result[key] = item;
    }
  });
  return result;
};

const hasOwnProperties = (value: Record<string, unknown>) => (
  Object.keys(value).length > 0
);

const getSchemaPassthroughProps = (
  schema: IFormilySchemaNode | IFormilySchemaDocument,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(schema).forEach(([key, item]) => {
    if (!INTERNAL_PROP_NAMES.has(key) && !RESERVED_FORMILY_SCHEMA_PROP_NAMES.has(key) && item !== undefined) {
      result[key] = item;
    }
  });
  return result;
};

const getRootSchemaPassthroughProps = (
  schema: IFormilySchemaDocument,
): Record<string, unknown> => ({
  ...getSchemaPassthroughProps(schema),
  ...(schema.title !== undefined ? { title: schema.title } : {}),
});

const getTreeRootSchemaProps = (
  props: Record<string, unknown>,
): Record<string, unknown> => ({
  ...pickSchemaProps(props),
  ...(props.title !== undefined ? { title: props.title } : {}),
});

const mergeRequiredKeys = (
  rootRequired: unknown,
  schemaRequired: unknown,
  propertyKeys: string[],
) => {
  const required = getRequiredKeySet(rootRequired);
  propertyKeys.forEach(key => required.delete(key));
  getRequiredKeySet(schemaRequired).forEach(key => required.add(key));
  return required.size ? Array.from(required) : undefined;
};

const mergeRootSchemaProps = (
  schema: IFormilySchemaDocument,
  rootProps: Record<string, unknown>,
): IFormilySchemaDocument => {
  const required = mergeRequiredKeys(
    rootProps.required,
    schema.required,
    Object.keys(schema.properties ?? {}),
  );
  const merged = {
    ...rootProps,
    ...schema,
  };

  if (required) {
    merged.required = required;
  } else {
    delete merged.required;
  }

  return merged;
};

const DECORATOR_COL_PROP_NAMES = [
  'labelCol',
  'wrapperCol',
];
const FORM_COL_PROP_NAMES = [
  'labelCol',
  'wrapperCol',
];

const normalizeDecoratorColForSchema = (value: unknown) => {
  if (typeof value === 'number') {
    return value > 0 ? { span: value } : undefined;
  }
  return value;
};

const normalizeDecoratorColForProps = (value: unknown) => {
  if (isRecord(value) && typeof value.span === 'number') {
    return value.span;
  }
  return value;
};

const normalizeFormColForSchema = normalizeDecoratorColForSchema;

const normalizeFormColForProps = normalizeDecoratorColForProps;

const normalizeRequiredMarkForSchema = (value: unknown) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

const applyDecoratorPropsAliasesToSchema = (
  schemaProps: Record<string, unknown>,
) => {
  const decoratorProps = schemaProps['x-decorator-props'];
  if (!isRecord(decoratorProps)) return schemaProps;

  const nextDecoratorProps = { ...decoratorProps };
  DECORATOR_COL_PROP_NAMES.forEach((key) => {
    const value = normalizeDecoratorColForSchema(nextDecoratorProps[key]);
    if (value === undefined) {
      delete nextDecoratorProps[key];
    } else {
      nextDecoratorProps[key] = value;
    }
  });
  return {
    ...schemaProps,
    'x-decorator-props': nextDecoratorProps,
  };
};

const applyDecoratorSchemaAliasesToProps = (
  schemaProps: Record<string, unknown>,
) => {
  const decoratorProps = schemaProps['x-decorator-props'];
  if (!isRecord(decoratorProps)) return schemaProps;

  const nextDecoratorProps = { ...decoratorProps };
  DECORATOR_COL_PROP_NAMES.forEach((key) => {
    nextDecoratorProps[key] = normalizeDecoratorColForProps(nextDecoratorProps[key]);
  });
  return {
    ...schemaProps,
    'x-decorator-props': nextDecoratorProps,
  };
};

const normalizeFormPropsForSchema = (
  props: Record<string, unknown>,
) => {
  const nextProps = { ...props };
  FORM_COL_PROP_NAMES.forEach((key) => {
    const value = normalizeFormColForSchema(nextProps[key]);
    if (value === undefined) {
      delete nextProps[key];
    } else {
      nextProps[key] = value;
    }
  });
  if (nextProps.requiredMark !== undefined) {
    nextProps.requiredMark = normalizeRequiredMarkForSchema(nextProps.requiredMark);
  }
  return nextProps;
};

const normalizeFormPropsForTree = (
  props: Record<string, unknown>,
) => {
  const nextProps = { ...props };
  FORM_COL_PROP_NAMES.forEach((key) => {
    nextProps[key] = normalizeFormColForProps(nextProps[key]);
  });
  return nextProps;
};

const normalizeComponentPropsForSchema = (
  componentName: string,
  props: Record<string, unknown>,
) => (
  componentName === 'Form' ? normalizeFormPropsForSchema(props) : props
);

const normalizeComponentPropsForTree = (
  componentName: string,
  props: Record<string, unknown>,
) => (
  componentName === 'Form' ? normalizeFormPropsForTree(props) : props
);

const applyReadOnlyPropAliasToSchema = (
  schemaProps: Record<string, unknown>,
  componentProps: Record<string, unknown>,
) => {
  const nextSchemaProps = { ...schemaProps };
  if (typeof nextSchemaProps.readOnly === 'boolean' && nextSchemaProps['x-read-only'] === undefined) {
    nextSchemaProps['x-read-only'] = nextSchemaProps.readOnly;
    delete nextSchemaProps.readOnly;
  }
  if (componentProps.readOnly !== undefined && nextSchemaProps['x-read-only'] === undefined) {
    nextSchemaProps['x-read-only'] = componentProps.readOnly;
  }
  return nextSchemaProps;
};

const applyDisabledPropAliasToSchema = (
  schemaProps: Record<string, unknown>,
  componentProps: Record<string, unknown>,
) => {
  if (componentProps.disabled === undefined || schemaProps['x-disabled'] !== undefined) {
    return schemaProps;
  }
  return {
    ...schemaProps,
    'x-disabled': componentProps.disabled,
  };
};

const isHiddenDerivedFromVisibleAlias = (
  schemaProps: Record<string, unknown>,
  sourceProps: Record<string, unknown>,
) => (
  sourceProps[FORMILY_STATE_HIDDEN_FROM_VISIBLE_PROP] === true
  && schemaProps['x-visible'] === false
);

const applyHiddenPropAliasToSchema = (
  schemaProps: Record<string, unknown>,
  componentProps: Record<string, unknown>,
  sourceProps: Record<string, unknown>,
) => {
  const isHiddenDerivedFromVisible = isHiddenDerivedFromVisibleAlias(schemaProps, sourceProps);
  if (componentProps.hidden !== undefined && schemaProps['x-hidden'] === undefined) {
    if (isHiddenDerivedFromVisible) {
      const nextSchemaProps = { ...schemaProps };
      if (componentProps.hidden !== true) {
        delete nextSchemaProps['x-visible'];
      }
      return nextSchemaProps;
    }
    return {
      ...schemaProps,
      'x-hidden': componentProps.hidden,
    };
  }

  if (isHiddenDerivedFromVisible && componentProps.hidden !== true) {
    const nextSchemaProps = { ...schemaProps };
    delete nextSchemaProps['x-visible'];
    return nextSchemaProps;
  }

  return schemaProps;
};

const omitStateComponentProps = (componentProps: Record<string, unknown>) => {
  const nextComponentProps = { ...componentProps };
  delete nextComponentProps.disabled;
  delete nextComponentProps.hidden;
  delete nextComponentProps.readOnly;
  return nextComponentProps;
};

const applyStatePropAliasesToSchema = (
  schemaProps: Record<string, unknown>,
  componentProps: Record<string, unknown>,
  sourceProps: Record<string, unknown>,
) => {
  const readOnlySchemaProps = applyReadOnlyPropAliasToSchema(schemaProps, componentProps);
  const disabledSchemaProps = applyDisabledPropAliasToSchema(readOnlySchemaProps, componentProps);
  const nextSchemaProps = applyHiddenPropAliasToSchema(disabledSchemaProps, componentProps, sourceProps);
  return {
    componentProps: omitStateComponentProps(componentProps),
    schemaProps: nextSchemaProps,
  };
};

const applyStateSchemaAliasesToProps = (
  schemaProps: Record<string, unknown>,
  props: Record<string, unknown>,
) => {
  const nextSchemaProps = { ...schemaProps };
  const nextProps = { ...props };
  if (typeof nextSchemaProps['x-disabled'] === 'boolean') {
    nextProps.disabled = nextSchemaProps['x-disabled'];
    delete nextProps['x-disabled'];
  }
  if (typeof nextSchemaProps['x-hidden'] === 'boolean') {
    nextProps.hidden = nextSchemaProps['x-hidden'];
    delete nextProps['x-hidden'];
  }
  if (nextSchemaProps['x-visible'] === false) {
    nextProps.hidden = true;
    nextProps[FORMILY_STATE_HIDDEN_FROM_VISIBLE_PROP] = true;
  }
  if (typeof nextSchemaProps['x-read-only'] === 'boolean') {
    nextProps.readOnly = nextSchemaProps['x-read-only'];
    delete nextProps['x-read-only'];
  }
  return {
    props: nextProps,
    schemaProps: nextSchemaProps,
  };
};

const hasStaticRequiredValidator = (schemaProps: Record<string, unknown>) => {
  const validator = schemaProps['x-validator'];
  if (!Array.isArray(validator)) return false;
  return validator.some(item => (
    isRecord(item)
    && item.required === true
  ));
};

const getRequiredKeySet = (required: unknown) => (
  Array.isArray(required)
    ? new Set(required.filter((key): key is string => typeof key === 'string' && key.trim().length > 0))
    : new Set<string>()
);

const syncRequiredFromProperties = <T extends IFormilySchemaNode | IFormilySchemaDocument>(
  schema: T,
): T => {
  const propertyEntries = Object.entries(schema.properties ?? {});
  if (!propertyEntries.length) return schema;

  const propertyKeys = new Set(propertyEntries.map(([key]) => key));
  const required = getRequiredKeySet(schema.required);
  propertyKeys.forEach(key => required.delete(key));
  propertyEntries.forEach(([key, child]) => {
    if (child.required === true) {
      required.add(key);
    }
  });

  const nextSchema = { ...schema };
  if (required.size > 0) return { ...nextSchema, required: Array.from(required) };
  delete nextSchema.required;
  return nextSchema;
};

/** 校验单个 Formily 节点的组件名、装饰器名及其属性对象形状。 */
const assertSchemaPropsFields = (schema: Record<string, unknown>, path: string) => {
  [
    'x-component',
    'x-decorator',
  ].forEach((key) => {
    if (schema[key] !== undefined && typeof schema[key] !== 'string') {
      throw new Error(`${path}.${key} 必须是字符串`);
    }
  });
  [
    'x-component-props',
    'x-decorator-props',
  ].forEach((key) => {
    if (schema[key] !== undefined && !isRecord(schema[key])) {
      throw new Error(`${path}.${key} 必须是对象`);
    }
  });
};

/** 按冻结的 Formily 结构键递归校验节点，阻止非法外部文档进入转换与渲染。 */
function assertSchemaNode(
  schema: unknown,
  path: string,
): asserts schema is IFormilySchemaNode {
  if (!isRecord(schema)) {
    throw new Error(`${path} 必须是对象`);
  }

  assertSchemaPropsFields(schema, path);

  FORMILY_SCHEMA_RECORD_CHILD_KEYS.forEach((key) => {
    const children = schema[key];
    if (children === undefined) return;
    if (!isRecord(children)) {
      throw new Error(`${path}.${key} 必须是对象`);
    }
    Object.entries(children).forEach(([childKey, child]) => {
      const childPath = path === 'root' && key === 'properties'
        ? `properties.${childKey}`
        : `${path}.${key}.${childKey}`;
      assertSchemaNode(child, childPath);
    });
  });

  FORMILY_SCHEMA_SINGLE_CHILD_KEYS.forEach((key) => {
    const child = schema[key];
    if (child === undefined) return;
    // additionalProperties/additionalItems 允许 JSON Schema 标准布尔开关，其余结构必须是可递归节点。
    if (
      (key === 'additionalProperties' || key === 'additionalItems')
      && typeof child === 'boolean'
    ) return;
    assertSchemaNode(child, `${path}.${key}`);
  });

  FORMILY_SCHEMA_ARRAY_CHILD_KEYS.forEach((key) => {
    const children = schema[key];
    if (children === undefined) return;
    if (!Array.isArray(children)) {
      throw new Error(`${path}.${key} 必须是数组`);
    }
    children.forEach((child, index) => {
      assertSchemaNode(child, `${path}.${key}.${index}`);
    });
  });

  const { items } = schema;
  if (items === undefined) return;
  if (Array.isArray(items)) {
    items.forEach((child, index) => {
      assertSchemaNode(child, `${path}.items.${index}`);
    });
    return;
  }
  assertSchemaNode(items, `${path}.items`);
}

/** 校验 Formily 根文档合同并递归覆盖所有受支持的子 Schema 结构。 */
function assertSchemaDocument(
  schema: unknown,
): asserts schema is IFormilySchemaDocument {
  assertSafeSchemaObjectKeys(schema, 'root');

  if (!isRecord(schema)) {
    throw new Error('Formily Schema 必须是对象');
  }

  if (schema.type !== 'object') {
    throw new Error('Formily Schema 根节点 type 必须是 object');
  }
  if (schema.properties !== undefined && !isRecord(schema.properties)) {
    throw new Error('Formily Schema 根节点 properties 必须是对象');
  }

  assertSchemaNode(schema, 'root');
}

/** 校验可独立复用的 Formily Schema 节点及其全部递归子节点。 */
export function assertFormilySchemaNode(
  schema: unknown,
  path = 'root',
): asserts schema is IFormilySchemaNode {
  assertSafeSchemaObjectKeys(schema, path);
  assertSchemaNode(schema, path);
}

/** 校验完整 Formily Schema 文档及其全部递归子节点。 */
export function assertFormilySchemaDocument(
  schema: unknown,
): asserts schema is IFormilySchemaDocument {
  assertSchemaDocument(schema);
}

function assertSchemaPackage(
  schema: unknown,
): asserts schema is IFormilySchemaPackage {
  assertSafeSchemaObjectKeys(schema, 'package');

  if (!isRecord(schema)) {
    throw new Error('Formily Schema 包装必须是对象');
  }

  if (schema.form !== undefined && !isRecord(schema.form)) {
    throw new Error('Formily Schema 包装 form 必须是对象');
  }

  assertSchemaDocument(schema.schema);
}

const isSchemaPackage = (schema: unknown): schema is IFormilySchemaPackage => (
  isRecord(schema)
  && schema.schema !== undefined
);

const isRootFormSchema = (schema: IFormilySchemaDocument) => (
  schema['x-component'] === 'Form'
);

const isScalarArrayItemSchema = (
  schema: IFormilySchemaNode,
  arrayItemComponentName: string,
) => (
  !schema.properties
  && !schema.items
  && schema['x-component'] !== arrayItemComponentName
  && schema.type !== 'object'
  && schema.type !== 'void'
);

const isIgnoredHistoricalSchemaNode = (schema: IFormilySchemaNode) => (
  typeof schema['x-component'] === 'string'
  && IGNORED_HISTORICAL_FORMILY_COMPONENT_NAMES.has(schema['x-component'])
);

const createSchemaDocumentFromTree = (
  tree: TreeNode,
  toSchemaNode: (node: TreeNode, index: number) => [string, IFormilySchemaNode],
): IFormilySchemaDocument => {
  const rootProps = clonePlainValue(tree.props ?? {});
  const schema: IFormilySchemaDocument & { properties: Record<string, IFormilySchemaNode> } = {
    ...pickSchemaProps(rootProps),
    ...(rootProps.title !== undefined ? { title: rootProps.title } : {}),
    type: 'object',
    properties: {},
  };

  tree.children?.forEach((child, index) => {
    const [key, childSchema] = toSchemaNode(child, index);
    schema.properties[getUniqueSchemaKey(schema.properties, key)] = childSchema;
  });
  return syncRequiredFromProperties(schema);
};

/**
 * 创建 Formily 风格 Schema 转换器。
 *
 * 用于把设计器 TreeNode 转为低码消费更常见的 `type/properties/x-component`
 * 结构，同时支持从该结构导回 TreeNode。
 */
export const createFormilySchemaTransformer = (
  options: IFormilySchemaTransformerOptions = {},
): ISchemaTransformer<IFormilySchemaInput> => {
  const {
    rootComponentName = 'Root',
    componentTypes = DEFAULT_COMPONENT_TYPES,
    componentDecorators = DEFAULT_COMPONENT_DECORATORS,
    componentNameAliases = DEFAULT_FORMILY_COMPONENT_NAME_ALIASES,
    componentNameByType = DEFAULT_COMPONENT_NAME_BY_TYPE,
    arrayItemComponentName = 'ArrayItem',
    fieldNamePrefix = 'field',
  } = options;

  const normalizeComponentName = (componentName: string) => (
    componentNameAliases[componentName] ?? componentName
  );

  const getHistoricalFieldComponentName = (
    props: Record<string, unknown>,
  ) => {
    const componentName = getStringProp(props, 'x-component');
    return componentName ? normalizeComponentName(componentName) : undefined;
  };

  const resolveTreeComponentName = (
    componentName: string,
    props: Record<string, unknown>,
  ) => {
    const normalizedComponentName = normalizeComponentName(componentName);
    if (normalizedComponentName !== HISTORICAL_FIELD_COMPONENT_NAME) {
      return normalizedComponentName;
    }

    const fieldComponentName = getHistoricalFieldComponentName(props);
    if (fieldComponentName) return fieldComponentName;

    const schemaType = getStringProp(props, 'type');
    return schemaType && componentNameByType[schemaType]
      ? componentNameByType[schemaType]
      : normalizedComponentName;
  };

  const getTreeComponentProps = (
    sourceComponentName: string,
    componentName: string,
    props: Record<string, unknown>,
  ) => {
    const baseProps = omitSchemaProps(props, componentName);
    if (normalizeComponentName(sourceComponentName) !== HISTORICAL_FIELD_COMPONENT_NAME) {
      return baseProps;
    }

    const componentProps = {
      ...clonePlainRecord(props['x-component-props']),
      ...baseProps,
    };
    delete componentProps.type;
    delete componentProps['x-component'];
    delete componentProps['x-component-props'];
    return componentProps;
  };

  const getTreeSchemaType = (
    componentName: string,
    props: Record<string, unknown>,
  ) => (
    getExplicitSchemaType(props)
    ?? (normalizeComponentName(componentName) === HISTORICAL_FIELD_COMPONENT_NAME
      ? getStringProp(props, 'type')
      : undefined)
  );

  const inferComponentName = (key: string, schema: IFormilySchemaNode): string => {
    if (typeof schema['x-component'] === 'string' && schema['x-component']) {
      return normalizeComponentName(schema['x-component']);
    }

    if (typeof schema.type === 'string' && componentNameByType[schema.type]) {
      return componentNameByType[schema.type];
    }

    return key;
  };

  const normalizeArrayItemSchema = (itemSchema: IFormilySchemaNode): IFormilySchemaNode => (
    itemSchema.type === 'void'
      ? { ...itemSchema, type: 'object' }
      : itemSchema
  );

  const getArrayItemSchemaType = (
    explicitSchemaType: string | undefined,
  ) => {
    if (explicitSchemaType) return explicitSchemaType;
    return componentTypes[arrayItemComponentName] === 'array' ? 'array' : 'object';
  };

  const appendSchemaProperties = (
    schemaNode: IFormilySchemaNode,
    children: TreeNode[],
  ) => {
    if (!children.length) return schemaNode;

    const properties: Record<string, IFormilySchemaNode> = {};
    children.forEach((child, childIndex) => {
      const [childKey, childSchema] = toSchemaNode(child, childIndex);
      properties[getUniqueSchemaKey(properties, childKey)] = childSchema;
    });
    return syncRequiredFromProperties({
      ...schemaNode,
      properties,
    });
  };

  const toSyntheticArrayItemSchema = (
    node: TreeNode,
    props: Record<string, unknown>,
    rawSchemaProps: Record<string, unknown>,
  ) => {
    if (props[FORMILY_SCALAR_ARRAY_ITEM_PROP] === true && node.children?.[0]) {
      const [, itemSchema] = toSchemaNode(node.children[0], 0);
      const nextItemSchema = { ...itemSchema };
      delete nextItemSchema['x-index'];
      delete nextItemSchema['x-designable-id'];
      return nextItemSchema;
    }

    const explicitSchemaType = getTreeSchemaType(node.componentName, props);
    const itemSchemaNode: IFormilySchemaNode = omitUndefined({
      ...rawSchemaProps,
      type: getArrayItemSchemaType(explicitSchemaType),
      title: typeof props.title === 'string' ? props.title : undefined,
    }) as IFormilySchemaNode;
    return appendSchemaProperties(itemSchemaNode, node.children ?? []);
  };

  const getArrayExportChildren = (children: TreeNode[]) => ({
    arrayItemChild: children.find(child => child.componentName === arrayItemComponentName),
    propertyChildren: children.filter(child => child.componentName !== arrayItemComponentName),
    syntheticArrayItemChildren: children.filter(child => (
      child.componentName === arrayItemComponentName
      && child.props?.[FORMILY_SYNTHETIC_ARRAY_ITEM_PROP] === true
    )),
  });

  const applyArrayItemsToSchema = (
    schemaNode: IFormilySchemaNode,
    children: TreeNode[],
  ) => {
    const {
      arrayItemChild,
      syntheticArrayItemChildren,
    } = getArrayExportChildren(children);

    if (syntheticArrayItemChildren.length > 1) {
      return {
        ...schemaNode,
        items: syntheticArrayItemChildren.map((child, childIndex) => {
          const [, itemSchema] = toSchemaNode(child, childIndex);
          return normalizeArrayItemSchema(itemSchema);
        }),
      };
    }

    if (!arrayItemChild) return schemaNode;
    const [, itemSchema] = toSchemaNode(arrayItemChild, 0);
    return {
      ...schemaNode,
      items: normalizeArrayItemSchema(itemSchema),
    };
  };

  const applySchemaNodeChildren = (
    schemaNode: IFormilySchemaNode,
    children: TreeNode[],
  ) => {
    if (!children.length) return schemaNode;
    if (schemaNode.type !== 'array') {
      return appendSchemaProperties(schemaNode, children);
    }

    const {
      propertyChildren,
    } = getArrayExportChildren(children);
    return appendSchemaProperties(
      applyArrayItemsToSchema(schemaNode, children),
      propertyChildren,
    );
  };

  const toSchemaNode = (node: TreeNode, index: number): [string, IFormilySchemaNode] => {
    const key = getSchemaKey(node, index, fieldNamePrefix);
    const props = clonePlainValue(node.props ?? {});
    const componentName = resolveTreeComponentName(node.componentName, props);
    const rawSchemaProps = pickSchemaProps(props, componentName);
    if (componentName === arrayItemComponentName && props[FORMILY_SYNTHETIC_ARRAY_ITEM_PROP] === true) {
      return [key, toSyntheticArrayItemSchema(node, props, rawSchemaProps)];
    }
    const componentProps = normalizeComponentPropsForSchema(
      componentName,
      getTreeComponentProps(node.componentName, componentName, props),
    );
    const decorator = componentDecorators[componentName];
    const schemaProps = decorator && rawSchemaProps['x-decorator'] === undefined
      ? { ...rawSchemaProps, 'x-decorator': decorator }
      : rawSchemaProps;
    const decoratorAliasedSchemaProps = applyDecoratorPropsAliasesToSchema(schemaProps);
    const {
      componentProps: schemaComponentProps,
      schemaProps: aliasedSchemaProps,
    } = applyStatePropAliasesToSchema(decoratorAliasedSchemaProps, componentProps, props);
    const explicitSchemaType = getTreeSchemaType(node.componentName, props);
    const schemaNode: IFormilySchemaNode = omitUndefined({
      ...aliasedSchemaProps,
      type: explicitSchemaType
        ? explicitSchemaType
        : inferComponentSchemaType(componentName, schemaComponentProps, componentTypes),
      title: typeof props.title === 'string' ? props.title : undefined,
      'x-component': componentName,
      'x-component-props': hasOwnProperties(schemaComponentProps) ? schemaComponentProps : undefined,
      'x-designable-id': node.id,
      'x-index': index,
    }) as IFormilySchemaNode;

    return [key, applySchemaNodeChildren(schemaNode, node.children ?? [])];
  };

  function toSchemaPropertiesTreeNodes(schema: IFormilySchemaNode) {
    return sortSchemaEntries(schema)
      .filter(([, childSchema]) => !isIgnoredHistoricalSchemaNode(childSchema))
      .map(([childKey, childSchema]) => (
        toTreeNodeSchema(childKey, childSchema, getRequiredKeySet(schema.required))
      ));
  }

  function toSyntheticArrayItemTreeNode(
    itemSchema: IFormilySchemaNode,
    itemKey: string,
  ): ITreeNode {
    const isScalarItem = isScalarArrayItemSchema(itemSchema, arrayItemComponentName);
    const itemProps = omitUndefined({
      ...getSchemaPassthroughProps(itemSchema),
      title: itemSchema.title,
      [FORMILY_SCALAR_ARRAY_ITEM_PROP]: isScalarItem ? true : undefined,
      [FORMILY_SYNTHETIC_ARRAY_ITEM_PROP]: true,
    });

    return {
      componentName: arrayItemComponentName,
      props: itemProps,
      children: itemSchema.properties
        ? toSchemaPropertiesTreeNodes(itemSchema)
        : [toTreeNodeSchema(itemKey, itemSchema)],
    };
  }

  function toArrayItemsTreeNodes(schema: IFormilySchemaNode): ITreeNode[] {
    if (schema.type !== 'array' || !schema.items) return [];

    if (Array.isArray(schema.items)) {
      return schema.items.map((itemSchema, itemIndex) => (
        toSyntheticArrayItemTreeNode(itemSchema, `items_${itemIndex}`)
      ));
    }

    if (inferComponentName('items', schema.items) === arrayItemComponentName) {
      return [toTreeNodeSchema('items', schema.items)];
    }

    return [toSyntheticArrayItemTreeNode(schema.items, 'value')];
  }

  function getDecoratedSchemaProps(
    componentName: string,
    rawSchemaProps: Record<string, unknown>,
  ) {
    const decorator = componentDecorators[componentName];
    if (!decorator || rawSchemaProps['x-decorator'] !== undefined) {
      return rawSchemaProps;
    }

    return { ...rawSchemaProps, 'x-decorator': decorator };
  }

  function getTreeRequiredProp(
    key: string,
    parentRequiredKeys: Set<string>,
    schemaProps: Record<string, unknown>,
    componentProps: Record<string, unknown>,
  ) {
    if (schemaProps.required !== undefined) return schemaProps.required;
    if (componentProps.required !== undefined) return componentProps.required;
    if (!parentRequiredKeys.has(key) && !hasStaticRequiredValidator(schemaProps)) return undefined;
    return true;
  }

  function getTreeNameProp(
    key: string,
    schema: IFormilySchemaNode,
    componentName: string,
    componentProps: Record<string, unknown>,
  ) {
    if (componentProps.name !== undefined) return componentProps.name;
    if (schema.type === 'void' || componentName === arrayItemComponentName) return undefined;
    return key;
  }

  function getTreeSchemaKeyProp(
    key: string,
    schema: IFormilySchemaNode,
    componentProps: Record<string, unknown>,
  ) {
    if (componentProps.name !== undefined || schema.type !== 'void') return undefined;
    return key;
  }

  function getTreeSchemaProps(
    key: string,
    schema: IFormilySchemaNode,
    componentName: string,
    parentRequiredKeys: Set<string>,
  ) {
    const rawSchemaProps = getSchemaPassthroughProps(schema);
    const schemaProps = getDecoratedSchemaProps(componentName, rawSchemaProps);
    const decoratorAliasedSchemaProps = applyDecoratorSchemaAliasesToProps(schemaProps);
    const componentProps = normalizeComponentPropsForTree(
      componentName,
      clonePlainValue(schema['x-component-props'] ?? {}),
    );
    const baseProps = omitUndefined({
      ...decoratorAliasedSchemaProps,
      ...componentProps,
      [FORMILY_SCHEMA_TYPE_PROP]: schema.type,
      required: getTreeRequiredProp(key, parentRequiredKeys, decoratorAliasedSchemaProps, componentProps),
      name: getTreeNameProp(key, schema, componentName, componentProps),
      [SCHEMA_KEY_PROP]: getTreeSchemaKeyProp(key, schema, componentProps),
      title: componentProps.title ?? schema.title,
    });
    const { props } = applyStateSchemaAliasesToProps(decoratorAliasedSchemaProps, baseProps);
    return props;
  }

  function toTreeNodeSchema(
    key: string,
    schema: IFormilySchemaNode,
    parentRequiredKeys: Set<string> = new Set(),
  ): ITreeNode {
    const componentName = inferComponentName(key, schema);

    return {
      id: schema['x-designable-id'],
      componentName,
      props: getTreeSchemaProps(key, schema, componentName, parentRequiredKeys),
      children: [
        ...toArrayItemsTreeNodes(schema),
        ...toSchemaPropertiesTreeNodes(schema),
      ],
    };
  }

  function getRootSchemaFormNode(tree: TreeNode) {
    if (tree.props?.[FORMILY_ROOT_SCHEMA_PROP] !== true) return undefined;
    return tree.children?.find(child => child.props?.[FORMILY_ROOT_SCHEMA_FORM_PROP] === true);
  }

  function getPackageRootFormNode(tree: TreeNode) {
    if (tree.props?.[FORMILY_PACKAGE_PROP] !== true) return undefined;
    return tree.children?.find(child => child.props?.[FORMILY_ROOT_FORM_PROP] === true);
  }

  function toRootFormSchemaDocument(tree: TreeNode, rootSchemaFormNode: TreeNode) {
    const schema = createSchemaDocumentFromTree(rootSchemaFormNode, toSchemaNode);
    const rootProps = clonePlainValue(tree.props ?? {});
    const formProps = normalizeFormPropsForSchema(
      omitInternalProps(clonePlainValue(rootSchemaFormNode.props ?? {})),
    );
    return {
      ...mergeRootSchemaProps(schema, getTreeRootSchemaProps(rootProps)),
      'x-component': rootSchemaFormNode.componentName,
      'x-component-props': hasOwnProperties(formProps) ? formProps : undefined,
    };
  }

  function toSchemaPackageDocument(tree: TreeNode, rootFormNode: TreeNode) {
    const schema = createSchemaDocumentFromTree(rootFormNode, toSchemaNode);
    const rootProps = clonePlainValue(tree.props ?? {});
    return {
      form: normalizeFormPropsForSchema(
        omitInternalProps(clonePlainValue(rootFormNode.props ?? {})),
      ),
      schema: mergeRootSchemaProps(schema, getTreeRootSchemaProps(rootProps)),
    };
  }

  return {
    toSchema(tree) {
      const rootSchemaFormNode = getRootSchemaFormNode(tree);
      const rootFormNode = getPackageRootFormNode(tree);

      if (rootSchemaFormNode) {
        return toRootFormSchemaDocument(tree, rootSchemaFormNode);
      }

      if (rootFormNode) {
        return toSchemaPackageDocument(tree, rootFormNode);
      }

      return createSchemaDocumentFromTree(tree, toSchemaNode);
    },
    toTreeNode(schema) {
      if (isSchemaPackage(schema)) {
        assertSchemaPackage(schema);
        return new TreeNode({
          componentName: rootComponentName,
          props: {
            ...getRootSchemaPassthroughProps(schema.schema),
            [FORMILY_PACKAGE_PROP]: true,
          },
          children: [
            {
              componentName: 'Form',
              props: {
                ...normalizeFormPropsForTree(clonePlainRecord(schema.form)),
                [FORMILY_ROOT_FORM_PROP]: true,
              },
              children: sortSchemaEntries(schema.schema).map(([key, childSchema]) => (
                toTreeNodeSchema(key, childSchema, getRequiredKeySet(schema.schema.required))
              )),
            },
          ],
        });
      }

      assertSchemaDocument(schema);
      if (isRootFormSchema(schema)) {
        return new TreeNode({
          componentName: rootComponentName,
          props: {
            ...getRootSchemaPassthroughProps(schema),
            [FORMILY_ROOT_SCHEMA_PROP]: true,
          },
          children: [
            {
              componentName: 'Form',
              props: {
                ...normalizeFormPropsForTree(clonePlainRecord(schema['x-component-props'])),
                [FORMILY_ROOT_SCHEMA_FORM_PROP]: true,
              },
              children: sortSchemaEntries(schema).map(([key, childSchema]) => (
                toTreeNodeSchema(key, childSchema, getRequiredKeySet(schema.required))
              )),
            },
          ],
        });
      }

      return new TreeNode({
        componentName: rootComponentName,
        props: getRootSchemaPassthroughProps(schema),
        children: sortSchemaEntries(schema).map(([key, childSchema]) => (
          toTreeNodeSchema(key, childSchema, getRequiredKeySet(schema.required))
        )),
      });
    },
  };
};
